import { axiosInstance } from 'src/util/axios/instance.axios'
import { OrganisationOverview, CreateTemplateDto, UpdateTemplateDto, SubmitResponsesDto, PerformanceReviewTemplate, PerformanceReviewResponse, PerformanceReviewComment, AnswerComment, UserInfo, PerformanceReviewInsightsDto } from '../model/my-team-performance'

const PERFORMANCE_REVIEW_BASE_URL = '/performance-review'

export async function getOrganisationOverview(): Promise<OrganisationOverview[]> {
  const { data } = await axiosInstance.request<OrganisationOverview[]>({
    url: '/my-team/organisation-overview',
  })

  return data
}

export async function createPerformanceReviewTemplate(dto: CreateTemplateDto): Promise<void> {
  await axiosInstance.request({
    method: 'POST',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template`,
    data: dto,
  })
}

export async function getAllPerformanceReviewTemplates(): Promise<PerformanceReviewTemplate[]> {
  const { data } = await axiosInstance.request<PerformanceReviewTemplate[]>({
    method: 'GET',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template`,
  })

  return data
}

export async function getMeetingById(id: string): Promise<PerformanceReviewTemplate> {
  const { data } = await axiosInstance.request<PerformanceReviewTemplate>({
    method: 'GET',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template/${id}`,
  })

  return data
}

// Get participants for a template using target_id
export async function getTemplateParticipants(id: string): Promise<UserInfo[]> {
  const { data } = await axiosInstance.request<UserInfo[]>({
    method: 'GET',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template/${id}/participants`,
  })

  return data
}

export async function getPerformanceReviewTemplatesByUser(userId?: string): Promise<PerformanceReviewTemplate[]> {
  const url = userId ? `${PERFORMANCE_REVIEW_BASE_URL}/template/user/${userId}` : `${PERFORMANCE_REVIEW_BASE_URL}/template/user`
  const { data } = await axiosInstance.request<PerformanceReviewTemplate[]>({
    method: 'GET',
    url,
  })

  return data
}

export async function updatePerformanceReviewTemplate(id: string, dto: UpdateTemplateDto): Promise<PerformanceReviewTemplate> {
  const { data } = await axiosInstance.request<PerformanceReviewTemplate>({
    method: 'PATCH',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template/${id}`,
    data: dto,
  })

  return data
}

export async function deletePerformanceReviewTemplate(id: string): Promise<{ message: string }> {
  const { data } = await axiosInstance.request<{ message: string }>({
    method: 'DELETE',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template/${id}`,
  })

  return data
}

export async function createPerformanceReviewResponses(dto: SubmitResponsesDto): Promise<PerformanceReviewResponse[]> {
  const { data } = await axiosInstance.request<PerformanceReviewResponse[]>({
    method: 'POST',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/responses`,
    data: dto,
  })

  return data
}

export async function getUserResponses(templateId: string, userId: string): Promise<PerformanceReviewResponse[]> {
  const { data } = await axiosInstance.request<PerformanceReviewResponse[]>({
    method: 'GET',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template/${templateId}/responses/${userId}`,
  })

  return data
}

export async function getPerformanceReviewComment(templateId: string, userId: string): Promise<PerformanceReviewComment | null> {
  const { data } = await axiosInstance.request<PerformanceReviewComment | null>({
    method: 'GET',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template/${templateId}/comment/${userId}`,
  })

  return data
}

export async function savePerformanceReviewComment(templateId: string, userId: string, content: string): Promise<void> {
  await axiosInstance.request({
    method: 'PUT',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template/${templateId}/comment/${userId}`,
    data: { content },
  })
}

export async function getAnswerComments(templateId: string, userId: string): Promise<AnswerComment[]> {
  const { data } = await axiosInstance.request<AnswerComment[]>({
    method: 'GET',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template/${templateId}/answer-comments/${userId}`,
  })

  return data
}

export async function getMyAnswerComments(templateId: string): Promise<AnswerComment[]> {
  const { data } = await axiosInstance.request<AnswerComment[]>({
    method: 'GET',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template/${templateId}/my-answer-comments`,
  })

  return data
}

export async function saveAnswerComment(templateId: string, userId: string, questionId: string, content: string): Promise<void> {
  await axiosInstance.request({
    method: 'PUT',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template/${templateId}/answer-comments/${userId}`,
    data: { questionId, content },
  })
}

export async function downloadPerformanceReviewPdf(id: string): Promise<ArrayBuffer> {
  const { data } = await axiosInstance.request<ArrayBuffer>({
    method: 'POST',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template/${id}/download-pdf`,
    responseType: 'arraybuffer',
  })

  return data
}

export async function getPerformanceReviewInsights(templateId: string): Promise<PerformanceReviewInsightsDto> {
  const { data } = await axiosInstance.request<PerformanceReviewInsightsDto>({
    method: 'GET',
    url: `${PERFORMANCE_REVIEW_BASE_URL}/template/${templateId}/insights`,
  })

  return data
}
